import React, { useEffect, useState } from 'react'
import { Button, Modal, TextField, TextInput } from '../../../../views/components'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import styles from './AccountStatusModal.module.scss'

export type TAccountStatusChange = {
  email: string
  reason: string
  clickupRef?: string
}

type TProps = {
  /** The account to change, and which way. Null is closed. */
  target: { email: string; enable: boolean } | null
  onCancel: () => void
  onConfirm: (change: TAccountStatusChange) => void
}

// Light theme with its own buttons: Modal.Body's actions cannot be disabled,
// and a reason is required before either change.
const AccountStatusModal = ({ target, onCancel, onConfirm }: TProps) => {
  const [reason, setReason] = useState('')
  const [clickupRef, setClickupRef] = useState('')

  useEffect(() => {
    setReason('')
    setClickupRef('')
  }, [target])

  const enable = target?.enable ?? false
  const canSubmit = reason.trim().length > 0

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!target || !canSubmit) return
    onConfirm({ email: target.email, reason: reason.trim(), clickupRef: clickupRef.trim() || undefined })
  }

  return (
    <Modal isOpen={!!target} padding="sm" width="sm" showClose={false}>
      <Modal.Header title={enable ? 'Enable this account?' : 'Disable this account?'} />
      <Modal.Body>
        {target && (
          <form className={styles.root} onSubmit={submit}>
            <p className={styles.copy}>
              {enable ? (
                <>
                  <strong>{target.email}</strong> will be able to log in again, to every agency they belong to.
                </>
              ) : (
                <>
                  <strong>{target.email}</strong> will be locked out of <strong>every</strong> agency they belong to,
                  not just one. To take away a single agency, use Remove instead.
                </>
              )}
            </p>
            <TextField
              label="Reason"
              inputComponent={
                <TextInput
                  type={TextFieldTypes.text}
                  name="statusReason"
                  autoComplete="off"
                  value={reason}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setReason(event.target.value)}
                />
              }
            />
            <TextField
              label="ClickUp ref (optional)"
              inputComponent={
                <TextInput
                  type={TextFieldTypes.text}
                  name="statusClickupRef"
                  autoComplete="off"
                  value={clickupRef}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setClickupRef(event.target.value)}
                />
              }
            />
            <div className={styles.actions}>
              <Button type="button" secondary onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {enable ? 'Enable account' : 'Disable account'}
              </Button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default AccountStatusModal
